import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Search, Filter, Eye, Pencil, MoreVertical, KeyRound, ShieldOff, UserCheck, UserX, UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PAGE_SIZE = 15;
const STAFF_ROLES = ["hospital_admin", "faculty", "department_head", "student"];
const STATUSES = ["active", "inactive", "suspended", "pending_verification"];

export default function StaffListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRole = searchParams.get("role") || "all";
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState(initialRole);
  const [status, setStatus] = useState("all");
  const [departmentId, setDepartmentId] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const navigate = useNavigate();

  // Keep the URL in sync when the role filter changes so the browser's
  // back-button and shared links preserve the current view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (role === "all") next.delete("role"); else next.set("role", role);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [role, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      if (departmentId !== "all") params.department_id = departmentId;
      const { data } = await api.get("/org/staff", { params });
      setUsers(data.data.users);
      setTotal(data.data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, role, status, departmentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/departments");
        setDepartments(data.data.departments);
      } catch { /* silent */ }
    })();
  }, []);

  const clearFilters = () => {
    setSearch(""); setRole("all"); setStatus("all"); setDepartmentId("all"); setPage(1);
  };

  const applyStatusChange = async () => {
    if (!confirm) return;
    try {
      const body = { account_status: confirm.nextStatus };
      if (confirm.nextStatus === "suspended") body.reason = confirm.reason || "Manual suspension";
      await api.patch(`/org/staff/${confirm.user.id}/status`, body);
      toast.success(`Status updated to ${confirm.nextStatus.replace(/_/g, " ")}.`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeFilters = useMemo(() => {
    return [role, status, departmentId, search].filter((v) => v && v !== "all").length;
  }, [role, status, departmentId, search]);

  return (
    <DashboardLayout title="All Staff" breadcrumbs={["My Institute", "Staff"]}>
      <div className="space-y-4" data-testid={ADMIN.staffListRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search by name, email, mobile or employee ID"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                data-testid={ADMIN.staffSearch}
              />
            </div>
            <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid={ADMIN.staffRoleFilter}><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid={ADMIN.staffStatusFilter}><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={departmentId} onValueChange={(v) => { setDepartmentId(v); setPage(1); }}>
              <SelectTrigger className="w-56" data-testid={ADMIN.staffDeptFilter}><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeFilters > 0 && (
              <button className="text-xs font-semibold text-slate-500 hover:text-[#101876]" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Link to="/app/hospital/staff/import" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid={ADMIN.staffImportBtn}>
                <UploadCloud className="h-4 w-4" /> Bulk upload
              </Link>
              <button
                onClick={() => navigate("/app/hospital/staff/new")}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]"
                data-testid={ADMIN.staffAddBtn}
              >
                <Plus className="h-4 w-4" /> Add staff
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <LoadingBlock label="Loading staff…" />
          ) : users.length === 0 ? (
            <EmptyState title="No staff match those filters" description="Try clearing filters or adding your first staff member." icon={Filter} />
          ) : (
            <Table data-testid={ADMIN.staffTable}>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Staff</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Emp. ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Login ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Department</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Designation</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Login</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={ADMIN.staffRow(u.id)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#101876] text-xs font-bold text-white">
                          {(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{u.full_name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{u.employee_id || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-700" data-testid={`staff-login-id-${u.id}`}>{u.login_id || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{ROLE_LABELS[u.role_code] || u.role_code}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.department_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.designation || "—"}</TableCell>
                    <TableCell><StatusBadge status={u.account_status} /></TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs ${u.login_created ? "text-emerald-700" : "text-slate-400"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${u.login_created ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {u.login_created ? "Yes" : "No"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/app/hospital/staff/${u.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.staffRowView(u.id)}>
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link to={`/app/hospital/staff/${u.id}/edit`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.staffRowEdit(u.id)}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.staffRowMore(u.id)}>
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel className="text-xs text-slate-500">Manage staff</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {u.account_status !== "active" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "active", title: `Activate ${u.full_name}?`, destructive: false, confirmLabel: "Activate" })}>
                                <UserCheck className="mr-2 h-4 w-4" /> Activate
                              </DropdownMenuItem>
                            )}
                            {u.account_status !== "inactive" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "inactive", title: `Deactivate ${u.full_name}?`, destructive: true, confirmLabel: "Deactivate" })}>
                                <UserX className="mr-2 h-4 w-4" /> Deactivate
                              </DropdownMenuItem>
                            )}
                            {u.account_status !== "suspended" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "suspended", title: `Suspend ${u.full_name}?`, destructive: true, confirmLabel: "Suspend", reason: "Manual suspension" })}>
                                <ShieldOff className="mr-2 h-4 w-4" /> Suspend
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                              <Link to={`/app/hospital/staff/${u.id}?reset=1`}><KeyRound className="mr-2 h-4 w-4" /> Reset password</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const { api } = await import("@/lib/api");
                                await api.post("/auth/admin/unlock-user", { email: u.email });
                                const { toast } = await import("sonner");
                                toast.success(`${u.full_name} can sign in again — lockout cleared.`);
                              } catch (err) {
                                const { formatApiError } = await import("@/lib/api");
                                const { toast } = await import("sonner");
                                toast.error(formatApiError(err));
                              }
                            }}
                              data-testid={`staff-unlock-${u.id}`}>
                              <UserCheck className="mr-2 h-4 w-4" /> Unlock login (clear lockout)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="staff-pagination" />
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title}
        description={`This action will change the account status to ${confirm?.nextStatus?.replace(/_/g, " ")}.`}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        onConfirm={applyStatusChange}
        testKey="staff-status-confirm"
      />
    </DashboardLayout>
  );
}
